/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/B2BEventParticipatingCompanyForm/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useCallback } from "react";
import Select from "react-select";

import styles from "../../modals/B2BEventParticipatingCompanyModal/index.module.css";

import { CircularProgress, Switch } from "@mui/material";
import { ReactComponent as SaveIcon } from "../../assets/icons/CompanyEditPage/SaveIcon.svg";
import useB2BParticipatingCompanyForm, {
  filterCompanyOption,
  getCompanyLabel,
} from "../../hooks/useB2BParticipatingCompanyForm.js";
import { companySelectStyles } from "./companySelectStyles.js";
import ParticipantUsersSection from "./ParticipantUsersSection.jsx";

/**
 * Shared add/edit participating company form (company select, executives, schedules).
 * Used inside the desktop modal or the tablet/mobile full-page view.
 *
 * @param {Object} props
 * @param {boolean} props.active - When false, internal state is cleared.
 * @param {"modal"|"view"} [props.variant="modal"] - Layout tweaks for full-page view.
 * @param {string} [props.eventId]
 * @param {string} [props.eventDateStart]
 * @param {string} [props.eventDateEnd]
 * @param {string} [props.eventTimeStart]
 * @param {string} [props.eventTimeEnd]
 * @param {Array} [props.participatingCompanies]
 * @param {Array} [props.buyerCompanies]
 * @param {Object} [props.selectedParticipant]
 * @param {string} [props.error]
 * @param {function} props.onCancel
 * @param {function} props.onSuccess - `(company, customerUsers, isEdit, activo) => void | Promise`
 */
const B2BEventParticipatingCompanyForm = (props) => {
  const form = useB2BParticipatingCompanyForm(props);
  const {
    t,
    active,
    variant,
    error,
    onCancel,
    isEditMode,
    selectedCompany,
    companyUsers,
    loadingUsers,
    selectedCustomerUsers,
    isSaving,
    companyActive,
    availableCompanies,
    hasAnyValidationError,
    isUserSelected,
    getSchedulesForUser,
    handleCheckboxChange,
    setScheduleForUserAt,
    removeScheduleForUser,
    addScheduleForUser,
    handleCompanySelectChange,
    handleCompanyActiveChange,
    getCompanyOptionValue,
    getNoCompaniesMessage,
    handleConfirm,
    eventDateStart,
    eventDateEnd,
    eventTimeStart,
    eventTimeEnd,
  } = form;

  const formatCompanyOptionLabel = useCallback(
    (option) => <span>{getCompanyLabel(option)}</span>,
    [],
  );

  if (!active) {
    return null;
  }

  const contentClassName =
    variant === "view"
      ? `${styles.modalContent} ${styles.modalContentView}`
      : styles.modalContent;

  return (
    <>
      <div className={contentClassName}>
        <label className={styles.modalTitle}>
          {isEditMode
            ? t("pages.b2bEventForm.editCompany")
            : t("pages.b2bEventForm.addCompany")}
        </label>
        <p className={styles.modalSubtitle}>
          {isEditMode
            ? t("pages.b2bEventForm.editCompanyModalDescription")
            : t("pages.b2bEventForm.addCompanyModalDescription")}
        </p>
        <div className={styles.companyNameWithActivoRow}>
          {isEditMode ? (
            <div className={styles.inputCol}>
              <label className={styles.inputLabel}>
                {t("components.addParticipantModal.company")}
              </label>
              <div className={styles.readOnlyCompanyName}>
                {getCompanyLabel(selectedCompany)}
              </div>
            </div>
          ) : (
            <div className={styles.inputCol}>
              <label className={styles.inputLabel}>
                {t("components.addParticipantModal.company")}
              </label>
              <Select
                options={availableCompanies}
                isSearchable
                isClearable
                value={selectedCompany}
                onChange={handleCompanySelectChange}
                placeholder={t("pages.b2bEventForm.searchCompanyPlaceholder")}
                noOptionsMessage={getNoCompaniesMessage}
                maxMenuHeight={200}
                styles={companySelectStyles}
                getOptionValue={getCompanyOptionValue}
                getOptionLabel={getCompanyLabel}
                filterOption={filterCompanyOption}
                formatOptionLabel={formatCompanyOptionLabel}
              />
            </div>
          )}
          {selectedCompany && (
            <div className={styles.activoSwitchCol}>
              <Switch
                checked={companyActive}
                onChange={handleCompanyActiveChange}
                inputProps={{
                  "aria-label": t("pages.b2bEventForm.activateCompany"),
                }}
              />
              <label className={styles.activoSwitchLabel}>
                {t("pages.b2bEventForm.activateCompany")}
              </label>
            </div>
          )}
        </div>

        {selectedCompany && (
          <div className={styles.customerUsersSection}>
            <div className={styles.customerUsersList}>
              <ParticipantUsersSection
                loadingUsers={loadingUsers}
                companyUsers={companyUsers}
                selectedCustomerUsers={selectedCustomerUsers}
                eventDateStart={eventDateStart}
                eventDateEnd={eventDateEnd}
                eventTimeStart={eventTimeStart}
                eventTimeEnd={eventTimeEnd}
                isUserSelected={isUserSelected}
                getSchedulesForUser={getSchedulesForUser}
                onCheckboxChange={handleCheckboxChange}
                onScheduleChange={setScheduleForUserAt}
                onRemoveSchedule={removeScheduleForUser}
                onAddSchedule={addScheduleForUser}
                styles={styles}
                t={t}
              />
            </div>
          </div>
        )}

        {error && (
          <span className={styles.errorMessage}>
            {typeof error === "string" && error.startsWith("error.")
              ? t(error)
              : error}
          </span>
        )}
      </div>
      <div className={styles.modalButtons}>
        <button
          type="button"
          onClick={onCancel}
          className={styles.cancelButton}
        >
          {t("components.addParticipantModal.cancelButton")}
        </button>
        <button
          type="button"
          className={styles.confirmButton}
          disabled={!selectedCompany || isSaving || hasAnyValidationError}
          onClick={handleConfirm}
        >
          {isSaving ? (
            <CircularProgress
              size={20}
              sx={{ color: "inherit" }}
              className={styles.confirmButtonSpinner}
            />
          ) : (
            <>
              <SaveIcon />
              {isEditMode
                ? t("pages.b2bEventForm.saveChanges")
                : t("components.addParticipantModal.addButton")}
            </>
          )}
        </button>
      </div>
    </>
  );
};

export default B2BEventParticipatingCompanyForm;
